import SettingsService from "@/services/settings/SettingsService";

/**
 * May this location sync to Unimicro — and when it may not, which of the four reasons is it?
 *
 * The sync pages used to answer that themselves, by asking `getUnimicroAccount` for a `clientId` and a
 * `clientSecret`. Those fields belonged to the per-location OAuth login that the server-client rebuild
 * removed, so the test could only ever be false: every Unimicro sync page refused to open, on a
 * connected and active location just the same. Nothing looked broken in the payload either, because
 * `getUnimicroAccount` still answers both names — as `""`, in the stub it returns for a location that
 * has no account at all.
 *
 * The authoritative gate is `UnimicroClientService.isActive`: a CompanyKey AND `purchaseStatus` ACTIVE.
 * `/api/unimicro/status` reports exactly that pair, so this asks the server the same question rather
 * than a second one that happens to agree with it.
 *
 * **The refusals are kept apart because they are different people's jobs.** Not connected is the
 * merchant's. Connected-but-not-active is a setup that stopped halfway, and must never read as "not
 * configured" — that sends someone back to redo a binding that already works. A missing server client
 * is CAGISTA's own, in System → Config, and no amount of clicking in Innstillinger will fix it.
 *
 * Wording follows the badges on the Unimicro settings page, so the two surfaces name the same state
 * the same way.
 */
export default {
  /**
   * Resolves `{active, message, showLink}` — never rejects, because a status call that fails is itself
   * one of the answers: refuse the sync and say why, rather than leaving the page on its spinner.
   */
  load(locationId) {
    return SettingsService.getUnimicroStatus(locationId).then(
        (response) => describe(response.data || {}),
        (error) => {
          console.log(error);
          return { active: false, message: 'Kunne ikke hente Unimicro-status. Prøv igjen.', showLink: false };
        }
    );
  }
};

function describe(status) {
  if (!status.configured) {
    return {
      active: false,
      message: 'CAGISTA er ikke satt opp mot Unimicro ennå. Ta kontakt med CAGISTA-support.',
      showLink: false
    };
  }
  if (!status.companyKeySet) {
    return { active: false, message: 'Denne avdelingen er ikke koblet til Unimicro.', showLink: true };
  }
  if (status.purchaseStatus === 'CANCELED') {
    return { active: false, message: 'Unimicro-integrasjonen er avsluttet for denne avdelingen.', showLink: true };
  }
  if (status.purchaseStatus !== 'ACTIVE') {
    const company = status.companyName ? '«' + status.companyName + '»' : 'Avdelingen';
    return {
      active: false,
      message: company + ' er koblet til Unimicro, men integrasjonen er ikke aktiv ennå'
          + ' (Aktivert – venter på fullføring).',
      showLink: true
    };
  }
  return { active: true, message: '', showLink: false };
}
